import { useEffect, useState } from 'react';
import { RELATION_TYPES } from '@/lib/relations';
import { ViewModeFieldset } from '@/components/ViewModeFieldset';
import { Pencil } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Field, Select, todayIso, useFormErrors } from '@/components/live';
import { DropZone } from '@/components/DropZone';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { RecordDrawer } from '@/components/RecordDrawer';
import { createRow, listRows, saveRow } from '@/api/resources';
import { uploadDocument } from '@/api/documents';
import { useAuth } from '@/auth/AuthProvider';
import { can, normaliseIdentifier } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { WORKER_TYPES } from '@/lib/practiceLists';
import { usePickers } from '@/hooks/usePickers';
import { WorkerAgreementsAndPayments } from '@/screens/parts/WorkerAgreements';

export interface WorkerRow {
  id: string;
  worker_code: string | null;
  contractor_id: string | null;
  contractor_name: string | null;
  contractor_code: string | null;
  full_name: string;
  gender: string | null;
  date_of_birth: string | null;
  age_years: number | null;
  father_or_spouse_name: string | null;
  relation_type?: string | null;
  phone: string | null;
  village_id: string | null;
  site_id?: string | null;
  site_name?: string | null;
  village_name: string | null;
  id_type: string | null;
  id_last4: string | null;
  skill: string;
  joined_on: string | null;
  left_on: string | null;
  daily_wage: number | null;
  ppe_issued: boolean;
  ppe_issued_on: string | null;
  safety_trained_on: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  photo_document_id: string | null;
  notes: string | null;
  status: string;
  is_active: boolean;
  worker_type: string;
  party_id: string | null;
  whatsapp_number: string | null;
  address: string | null;
  pin_code: string | null;
  id_document_id: string | null;
  active_agreements: number;
  total_paid: number;
  unconfirmed_payments: number;
}

export const SKILLS = [
  { id: 'feller', label: 'Feller / cutter' }, { id: 'loader', label: 'Loader' }, { id: 'driver', label: 'Driver' },
  { id: 'helper', label: 'Helper' }, { id: 'supervisor', label: 'Supervisor' }, { id: 'nursery', label: 'Nursery worker' },
  { id: 'sprayer', label: 'Sprayer' }, { id: 'other', label: 'Other' },
];
const GENDERS = [{ id: '', label: '--' }, { id: 'female', label: 'Female' }, { id: 'male', label: 'Male' }, { id: 'other', label: 'Other' }];
const ID_TYPES = [{ id: '', label: '--' }, { id: 'aadhaar', label: 'Aadhaar' }, { id: 'voter_id', label: 'Voter ID' },
  { id: 'ration_card', label: 'Ration card' }, { id: 'driving_licence', label: 'Driving licence' }, { id: 'pan', label: 'PAN' },
  { id: 'job_card', label: 'MGNREGA job card' }, { id: 'passport', label: 'Passport' }, { id: 'other', label: 'Other' }];

export function skillLabel(id?: string | null) {
  return SKILLS.find((entry) => entry.id === id)?.label ?? id ?? '--';
}

/** The latest date of birth that makes someone 18 today. */
function eighteenYearsAgo() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
}

/**
 * Add or edit a worker (owner, 2026-09-21: "worker inventory"). Through a contractor or engaged
 * directly. The database refuses anyone under 18 and anyone over the contractor's licensed number.
 */
const NEW_CONTRACTOR = '__new_contractor';

export function WorkerDrawer({ editing, contractorId, onClose }: {
  editing?: WorkerRow | null; contractorId?: string; onClose: () => void;
}) {
  const { profile } = useAuth();
  const sites = usePickers(['sites']);
  const mayWrite = can(profile, 'party.register', editing ? 'update' : 'create');
  const queryClient = useQueryClient();
  const { errors, check, clear } = useFormErrors();
  const [form, setForm] = useState(() => ({
    contractor_id: editing?.contractor_id ?? contractorId ?? '',
    full_name: editing?.full_name ?? '', gender: editing?.gender ?? '', date_of_birth: editing?.date_of_birth ?? '',
    father_or_spouse_name: editing?.father_or_spouse_name ?? '', relation_type: editing?.relation_type ?? '', phone: editing?.phone ?? '',
    id_type: editing?.id_type ?? '', id_last4: editing?.id_last4 ?? '', skill: editing?.skill ?? 'helper',
    joined_on: editing?.joined_on ?? todayIso(), left_on: editing?.left_on ?? '',
    daily_wage: editing?.daily_wage?.toString() ?? '', ppe_issued: editing?.ppe_issued ?? false,
    ppe_issued_on: editing?.ppe_issued_on ?? '', safety_trained_on: editing?.safety_trained_on ?? '',
    emergency_contact_name: editing?.emergency_contact_name ?? '', emergency_contact_phone: editing?.emergency_contact_phone ?? '',
    notes: editing?.notes ?? '', village_id: editing?.village_id ?? '', site_id: editing?.site_id ?? '',
    worker_type: editing?.worker_type ?? 'casual', whatsapp_number: editing?.whatsapp_number ?? '',
    address: editing?.address ?? '', pin_code: editing?.pin_code ?? '',
  }));
  const [idProof, setIdProof] = useState<File | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A saved record opens read-only; Edit unlocks it (owner, 2026-09-27).
  const [viewing, setViewing] = useState(Boolean(editing));
  const editingId = editing?.id;
  useEffect(() => { setViewing(Boolean(editingId)); }, [editingId]);
  const existingPhoto = usePhotoUrls([editing?.photo_document_id]);

  // "+ Add contractor" from the worker's own dropdown (owner, 2026-09-27: "add option to add
  // contractor from dropdown?"): the name and phone now, the licence and the rest on its own page.
  const mayAddContractor = can(profile, 'party.register', 'create');
  const [newContractor, setNewContractor] = useState<{ name: string; phone: string } | null>(null);
  const [addingContractor, setAddingContractor] = useState(false);
  async function addContractor() {
    if (!newContractor?.name.trim()) { toast.error('Give the contractor\'s name.'); return; }
    if (!form.village_id) { toast.error('Choose the worker\'s village first: the contractor is recorded there.'); return; }
    let phone: string | null = null;
    if (newContractor.phone.trim()) {
      const result = normaliseIdentifier('sms', newContractor.phone.trim());
      if ('error' in result) { toast.error(result.error); return; }
      phone = result.value;
    }
    setAddingContractor(true);
    try {
      const created = await createRow<{ id: string }>('contractors', { name: newContractor.name.trim(), phone, village_id: form.village_id });
      await queryClient.invalidateQueries({ queryKey: ['contractors'] });
      await contractors.refetch();
      setForm((now) => ({ ...now, contractor_id: created.id }));
      setNewContractor(null);
      toast.success('Contractor added.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The contractor could not be added.');
    } finally {
      setAddingContractor(false);
    }
  }
  const contractors = useQuery({
    queryKey: ['picker', 'contractors'],
    queryFn: async () => (await listRows<{ id: string; name: string; contractor_code: string; status: string; licence_state: string }>(
      'contractors', { orderBy: { column: 'name' } })),
    enabled: isSupabaseConfigured,
  });

  async function save() {
    const phone = form.phone.trim() ? normaliseIdentifier('sms', form.phone.trim()) : null;
    const emergency = form.emergency_contact_phone.trim() ? normaliseIdentifier('sms', form.emergency_contact_phone.trim()) : null;
    const whatsapp = form.whatsapp_number.trim() ? normaliseIdentifier('sms', form.whatsapp_number.trim()) : null;
    const ok = check({
      full_name: form.full_name.trim() ? undefined : 'Give the worker\'s name.',
      village_id: form.contractor_id || form.village_id ? undefined : 'Where does the worker live? (A contractor\'s worker takes the contractor\'s village.)',
      date_of_birth: !form.date_of_birth ? 'Date of birth is needed to confirm the worker is 18 or over.'
        : form.date_of_birth > eighteenYearsAgo() ? 'The worker must be at least 18.' : undefined,
      phone: phone && 'error' in phone ? phone.error : undefined,
      emergency_contact_phone: emergency && 'error' in emergency ? emergency.error : undefined,
      whatsapp_number: whatsapp && 'error' in whatsapp ? whatsapp.error : undefined,
      pin_code: !form.pin_code || /^[1-9][0-9]{5}$/.test(form.pin_code) ? undefined : 'A PIN code is 6 digits.',
      id_type: form.id_last4 && !form.id_type ? 'Which ID are the last four characters from?' : undefined,
      id_last4: !form.id_last4 || /^[0-9A-Za-z]{4}$/.test(form.id_last4) ? undefined : 'Only the last four characters.',
      left_on: form.left_on && form.joined_on && form.left_on < form.joined_on ? 'Cannot leave before joining.' : undefined,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const values = {
        contractor_id: form.contractor_id || null, village_id: form.village_id || null, site_id: form.site_id || null, full_name: form.full_name.trim(), gender: form.gender || null,
        date_of_birth: form.date_of_birth || null, father_or_spouse_name: form.father_or_spouse_name.trim() || null,
        relation_type: form.father_or_spouse_name.trim() ? form.relation_type || null : null,
        phone: phone && 'value' in phone ? phone.value : null, id_type: form.id_type || null, id_last4: form.id_last4 || null,
        skill: form.skill, joined_on: form.joined_on || null, left_on: form.left_on || null,
        daily_wage: form.daily_wage ? Number(form.daily_wage) : null, ppe_issued: form.ppe_issued,
        ppe_issued_on: form.ppe_issued ? form.ppe_issued_on || null : null, safety_trained_on: form.safety_trained_on || null,
        emergency_contact_name: form.emergency_contact_name.trim() || null,
        emergency_contact_phone: emergency && 'value' in emergency ? emergency.value : null, notes: form.notes.trim() || null,
        worker_type: form.worker_type, whatsapp_number: whatsapp && 'value' in whatsapp ? whatsapp.value : null,
        address: form.address.trim() || null, pin_code: form.pin_code || null,
      };
      const saved = editing ? await saveRow<WorkerRow>('workers', editing.id, values) : await createRow<WorkerRow>('workers', values);
      if (photo && profile?.organization) {
        try {
          const document = await uploadDocument({ file: photo, bucket: 'farmer-photos', organizationId: profile.organization.id, ownerId: saved.id });
          await saveRow('workers', saved.id, { photo_document_id: document.id });
        } catch (error) {
          toast.error(`Saved, but the photo was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      }
      if (idProof && profile?.organization) {
        try {
          const document = await uploadDocument({ file: idProof, bucket: 'kyc-documents', organizationId: profile.organization.id, ownerId: saved.id });
          await saveRow('workers', saved.id, { id_document_id: document.id });
        } catch (error) {
          toast.error(`Saved, but the ID proof was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['workers'] });
      await queryClient.invalidateQueries({ queryKey: ['contractors'] });
      toast.success(editing ? 'Saved.' : 'Worker added.');
      clear();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <RecordDrawer
      footer={
        <>
          {viewing ? (
            <>
              <button onClick={onClose} type="button">Close</button>
              {mayWrite && <button className="primary" onClick={() => setViewing(false)} type="button"><Pencil size={14} /> Edit</button>}
            </>
          ) : (
            <>
              <button className="cancel" onClick={onClose} type="button">Cancel</button>
              {mayWrite && (<button className="primary" disabled={saving} onClick={() => void save()} type="button">
                {saving ? 'Saving...' : editing ? 'Save changes' : 'Add worker'}
              </button>
              )}
            </>
          )}
        </>
      }
      onClose={onClose}
      open
      record={editing ? { table: 'ops.workers', id: editing.id, invalidate: ['workers'] } : null}
      subtitle={editing?.worker_code ?? 'Engaged through a contractor, or directly.'}
      title={editing ? editing.full_name : 'Add worker'}
    >
      <ViewModeFieldset viewing={viewing}>
      <div className="form-photo">
        <FarmerPhoto name={form.full_name} size={72}
                     url={preview ?? (editing?.photo_document_id ? existingPhoto.data?.[editing.photo_document_id] : null)} />
        <div className="form-photo-actions">
          <DropZone accept="image/*" buttonLabel={preview || editing?.photo_document_id ? 'Change photo' : 'Add photo'}
                    label="Drag a photo here, or"
                    onFile={(file) => { setPhoto(file); setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); }); }} />
        </div>
      </div>
      <Field label="Contractor">
        <Select disabled={Boolean(contractorId)}
                onChange={(value) => (value === NEW_CONTRACTOR ? setNewContractor({ name: '', phone: '' }) : setForm({ ...form, contractor_id: value }))}
                options={[{ id: '', label: 'Engaged directly (no contractor)' },
                  ...(contractors.data ?? []).map((row) => ({ id: row.id, label: `${row.name} (${row.contractor_code})${row.licence_state === 'expired' ? ' - licence expired' : ''}` })),
                  ...(mayAddContractor ? [{ id: NEW_CONTRACTOR, label: '+ Add contractor' }] : [])]}
                value={form.contractor_id} />
      </Field>
      {newContractor && (
        <div className="inline-form">
          <div className="field-row">
            <Field label="Contractor name" required>
              <input autoFocus onChange={(event) => setNewContractor({ ...newContractor, name: event.target.value })} value={newContractor.name} />
            </Field>
            <Field label="Phone">
              <input inputMode="tel" onChange={(event) => setNewContractor({ ...newContractor, phone: event.target.value })}
                     placeholder="98xxxxxx01" value={newContractor.phone} />
            </Field>
          </div>
          <small className="muted">Based in the worker's village below. Licence, GSTIN and the rest are added on the Contractors page.</small>
          <div className="inline-form-actions">
            <button className="cancel" onClick={() => setNewContractor(null)} type="button">Cancel</button>
            <button className="primary" disabled={addingContractor} onClick={() => void addContractor()} type="button">
              {addingContractor ? 'Adding...' : 'Add contractor'}
            </button>
          </div>
        </div>
      )}
      <Field label="Usual work site">
        <Select onChange={(value) => setForm({ ...form, site_id: value })}
                options={sites.sites} placeholder="No fixed site (farm work)" value={form.site_id} />
      </Field>
      {!form.contractor_id && (
        <GeographyPicker error={errors.village_id} onChange={(villageId) => setForm({ ...form, village_id: villageId })} value={form.village_id} />
      )}
      <Field error={errors.full_name} label="Name" required>
        <input onChange={(event) => setForm({ ...form, full_name: event.target.value })} value={form.full_name} />
      </Field>
      <div className="field-row">
        <Field label="Gender"><Select onChange={(value) => setForm({ ...form, gender: value })} options={GENDERS} value={form.gender} /></Field>
        <Field error={errors.date_of_birth} hint="18 or over only." label="Date of birth" required>
          <input max={eighteenYearsAgo()} onChange={(event) => setForm({ ...form, date_of_birth: event.target.value })} type="date" value={form.date_of_birth} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Relation">
          <Select onChange={(value) => setForm({ ...form, relation_type: value })} options={RELATION_TYPES} value={form.relation_type} />
        </Field>
        <Field label="Father's or spouse's name">
          <input onChange={(event) => setForm({ ...form, father_or_spouse_name: event.target.value })} value={form.father_or_spouse_name} />
        </Field>
        <Field error={errors.phone} label="Phone">
          <input inputMode="tel" onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="98xxxxxx01" value={form.phone} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Worker type"><Select onChange={(value) => setForm({ ...form, worker_type: value || 'casual' })} options={WORKER_TYPES} value={form.worker_type} /></Field>
        <Field error={errors.whatsapp_number} hint="Blank: the phone number. Codes and summaries go here." label="WhatsApp number">
          <input inputMode="tel" onChange={(event) => setForm({ ...form, whatsapp_number: event.target.value })} placeholder="Same as phone" value={form.whatsapp_number} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Address"><input onChange={(event) => setForm({ ...form, address: event.target.value })} placeholder="House, para / hamlet, post office" value={form.address} /></Field>
        <Field error={errors.pin_code} label="PIN code"><input inputMode="numeric" maxLength={6} onChange={(event) => setForm({ ...form, pin_code: event.target.value.trim() })} value={form.pin_code} /></Field>
      </div>
      <div className="field-row">
        <Field error={errors.id_type} label="ID proof type"><Select onChange={(value) => setForm({ ...form, id_type: value })} options={ID_TYPES} value={form.id_type} /></Field>
        <Field error={errors.id_last4} hint="Only the last four characters are kept." label="ID - last 4">
          <input maxLength={4} onChange={(event) => setForm({ ...form, id_last4: event.target.value.trim() })} value={form.id_last4} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Skill / role"><Select onChange={(value) => setForm({ ...form, skill: value })} options={SKILLS} value={form.skill} /></Field>
        <Field label="Daily wage (₹)">
          <input inputMode="decimal" onChange={(event) => setForm({ ...form, daily_wage: event.target.value })} value={form.daily_wage} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Joined on"><input onChange={(event) => setForm({ ...form, joined_on: event.target.value })} type="date" value={form.joined_on} /></Field>
        <Field error={errors.left_on} label="Left on"><input onChange={(event) => setForm({ ...form, left_on: event.target.value })} type="date" value={form.left_on} /></Field>
      </div>
      <Field hint={idProof ? `${idProof.name} will be uploaded.` : editing?.id_document_id ? 'An ID proof is on file. Choose a file to replace it.' : 'A photo or PDF of the ID.'} label="ID proof (scan)">
        <DropZone accept="image/*,application/pdf" buttonLabel={idProof ? 'Change file' : 'Choose file'} label="Drag the ID proof here, or" onFile={setIdProof} />
      </Field>
      <div className="section-heading"><h3>Health & safety</h3></div>
      <div className="field-row">
        <Field label="Protective equipment (PPE) issued">
          <label className="check-inline">
            <input checked={form.ppe_issued} onChange={(event) => setForm({ ...form, ppe_issued: event.target.checked })} type="checkbox" /> Issued
          </label>
        </Field>
        {form.ppe_issued && (
          <Field label="PPE issued on"><input onChange={(event) => setForm({ ...form, ppe_issued_on: event.target.value })} type="date" value={form.ppe_issued_on} /></Field>
        )}
      </div>
      <Field label="Safety training on">
        <input max={todayIso()} onChange={(event) => setForm({ ...form, safety_trained_on: event.target.value })} type="date" value={form.safety_trained_on} />
      </Field>
      <div className="field-row">
        <Field label="Emergency contact"><input onChange={(event) => setForm({ ...form, emergency_contact_name: event.target.value })} value={form.emergency_contact_name} /></Field>
        <Field error={errors.emergency_contact_phone} label="Emergency phone">
          <input inputMode="tel" onChange={(event) => setForm({ ...form, emergency_contact_phone: event.target.value })} value={form.emergency_contact_phone} />
        </Field>
      </div>
      <Field label="Notes"><textarea onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={2} value={form.notes} /></Field>
      {editing && <WorkerAgreementsAndPayments worker={editing} />}
      </ViewModeFieldset>
    </RecordDrawer>
  );
}
